//////////////////
// IMPORTS
//////////////////
// libraries
import { map } from "lodash";
import React, { useState } from "react";
// calendar-color-picker imports
import {
  IColorOptionProps,
  IMenuProps,
  INullOptionProps,
  IProps,
} from "./CalendarColorPickerTypes";
// util
import { useCalendarColors } from "@clockwise/web-commons/src/util/event-category-coloring";
// material-ui
import { ArrowDropDown, Block, Brightness1, CheckCircle } from "@clockwise/design-system/icons";
import { IconButton, Menu, Tooltip } from "@material-ui/core";
import classNames from "classnames";

/////////////////////
// COMMON COMPONENTS
/////////////////////
const NullColorOption = (props: INullOptionProps) => {
  const { active, onSelect, tooltipPosition, selectedColorIndex } = props;
  const defaultIsActive = !active || !selectedColorIndex;
  return (
    <Tooltip title="Default" placement={tooltipPosition || "top-end"}>
      <IconButton className="cw-w-8 cw-h-8 cw-p-0.5" onClick={() => onSelect(null)}>
        <Block
          className={classNames("cw-w-6 cw-h-6", {
            "cw-text-neutral": !defaultIsActive,
            "cw-text-destructive": defaultIsActive,
          })}
        />
      </IconButton>
    </Tooltip>
  );
};

const ColorOption = (props: IColorOptionProps) => {
  const { selectedColorIndex, active, onSelect, color, colorKey, tooltipPosition } = props;
  const colorIsActive = active && colorKey === selectedColorIndex;
  const Icon = colorIsActive ? CheckCircle : Brightness1;

  return (
    <Tooltip title={color.name} placement={tooltipPosition || "top-end"}>
      <IconButton onClick={() => onSelect(colorKey)} className="cw-w-8 cw-h-8 cw-p-0.5">
        <Icon className="cw-w-6 cw-h-6" style={{ color: color.foreground }} />
      </IconButton>
    </Tooltip>
  );
};

/////////////////////
// MENU COMPONENT
/////////////////////
export const CalendarColorPickerMenu = (props: IMenuProps) => {
  const { calendarColors, getColorsFromSetting } = useCalendarColors();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onClickToOpen = (event: React.MouseEvent<HTMLElement>) => {
    // This prevents ghost click.
    event.preventDefault();
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const onRequestClose = () => {
    setOpen(false);
  };

  const onSelect = (selectedColorIndex: string | null) => {
    onRequestClose();
    props.onSelect(selectedColorIndex);
  };

  const getTooltipPosition = (index: number): IColorOptionProps["tooltipPosition"] => {
    switch (index) {
      case 1:
        return "bottom-start";
      case 2:
      case 3:
        return "bottom";
      case 4:
        return "bottom-end";
      case 5:
      case 9:
        return "top-start";
      case 6:
      case 7:
      case 10:
      case 11:
        return "top";
      case 8:
      case 12:
      default:
        return "top-end";
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const color = getColorsFromSetting(props.eventCategoryColoring)?.foreground;
  const colorIsActive = !!color && props.eventCategoryColoring.active;

  return (
    <div className="cw-flex">
      <IconButton className="cw-text-muted hover:cw-text-muted-hover" onClick={onClickToOpen}>
        <ArrowDropDown />
        {!!color && colorIsActive ? <CheckCircle htmlColor={color} /> : <Block />}
      </IconButton>
      <Menu
        open={open}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={onRequestClose}
        onBackdropClick={onRequestClose}
        onEscapeKeyDown={onRequestClose}
      >
        <div className="cw-flex cw-flex-wrap cw-items-center cw-justify-center cw-max-w-[140px] cw-p-1">
          <NullColorOption {...props} tooltipPosition="bottom-end" onSelect={onSelect} />
          {Object.keys(calendarColors).map((key, i) => {
            const calColor = calendarColors[key];
            const tooltipPosition = getTooltipPosition(i + 1);
            return (
              <ColorOption
                {...props}
                key={key}
                tooltipPosition={tooltipPosition}
                color={calColor}
                colorKey={key}
                onSelect={onSelect}
              />
            );
          })}
        </div>
      </Menu>
    </div>
  );
};

/////////////////////
// LIST COMPONENT
/////////////////////
export const CalendarColorPicker = (props: IProps) => {
  const { calendarColors } = useCalendarColors();

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  return (
    <div className="cw-flex">
      <NullColorOption {...props} />
      {map(calendarColors, (color, key) => (
        <ColorOption key={key} color={color} colorKey={key} {...props} />
      ))}
    </div>
  );
};
